import type { ToolResult } from './types';
import { getAssistantRouteContext, getSuggestedPrompts } from './suggestedPrompts';

const MOCK_BOTS = [
  {
    id: 'bot-alpha-001',
    name: 'BTC Grid Alpha',
    strategy: 'Grid Trading',
    status: 'running',
    exchange: 'Binance',
    tradingPair: 'BTC/USDT',
    performance: { pnl: 1247.82, pnlPercent: 4.2, trades: 156, winRate: 0.62 },
  },
  {
    id: 'bot-beta-002',
    name: 'ETH Momentum Beta',
    strategy: 'Momentum',
    status: 'stopped',
    exchange: 'Kraken',
    tradingPair: 'ETH/USD',
    performance: { pnl: -89.5, pnlPercent: -1.1, trades: 42, winRate: 0.48 },
    errorMessage: 'API key permissions insufficient: trade scope missing',
    errorCode: 'GW-EXC-001',
  },
  {
    id: 'bot-gamma-003',
    name: 'SOL DCA',
    strategy: 'DCA',
    status: 'running',
    exchange: 'Coinbase',
    tradingPair: 'SOL/USDT',
    performance: { pnl: 312.15, pnlPercent: 2.8, trades: 28, winRate: 0.71 },
  },
];

export function mockGetMyBots(): ToolResult {
  return {
    tool: 'mockGetMyBots',
    executedAt: new Date().toISOString(),
    data: { bots: MOCK_BOTS },
  };
}

export function mockGetBotStatus(botId?: string): ToolResult {
  const bot = botId
    ? MOCK_BOTS.find(b => b.id === botId || b.name.toLowerCase().includes(botId.toLowerCase()))
    : MOCK_BOTS.find(b => b.status === 'stopped' || b.status === 'error') ?? MOCK_BOTS[0];

  return {
    tool: 'mockGetBotStatus',
    executedAt: new Date().toISOString(),
    data: bot ?? MOCK_BOTS[0],
  };
}

export function mockGetRecentOrders(): ToolResult {
  return {
    tool: 'mockGetRecentOrders',
    executedAt: new Date().toISOString(),
    data: {
      orders: [
        { tradeId: 'T-001', symbol: 'BTC/USDT', side: 'buy', quantity: 0.05, price: 67420.5, pnl: 124.3, executedAt: new Date(Date.now() - 3600_000).toISOString(), botId: 'bot-alpha-001' },
        { tradeId: 'T-002', symbol: 'BTC/USDT', side: 'sell', quantity: 0.05, price: 67890.0, pnl: 234.75, executedAt: new Date(Date.now() - 7200_000).toISOString(), botId: 'bot-alpha-001' },
        { tradeId: 'T-003', symbol: 'ETH/USD', side: 'buy', quantity: 1.2, price: 3542.0, pnl: -18.4, executedAt: new Date(Date.now() - 10800_000).toISOString(), botId: 'bot-beta-002' },
        { tradeId: 'T-004', symbol: 'SOL/USDT', side: 'buy', quantity: 10.0, price: 148.25, pnl: 42.5, executedAt: new Date(Date.now() - 14400_000).toISOString(), botId: 'bot-gamma-003' },
        { tradeId: 'T-005', symbol: 'BTC/USDT', side: 'sell', quantity: 0.03, price: 67150.0, pnl: -12.9, executedAt: new Date(Date.now() - 18000_000).toISOString(), botId: 'bot-alpha-001' },
      ],
    },
  };
}

export function mockGetPositions(): ToolResult {
  return {
    tool: 'mockGetPositions',
    executedAt: new Date().toISOString(),
    data: {
      positions: [
        { symbol: 'BTC/USDT', side: 'long', quantity: 0.15, avgEntryPrice: 66800.0, markPrice: 67420.5, unrealizedPnl: 93.08, pnlPercent: 0.93, venue: 'Binance' },
        { symbol: 'SOL/USDT', side: 'long', quantity: 25.0, avgEntryPrice: 145.0, markPrice: 148.25, unrealizedPnl: 81.25, pnlPercent: 2.24, venue: 'Coinbase' },
        { symbol: 'ETH/USD', side: 'short', quantity: 0.5, avgEntryPrice: 3580.0, markPrice: 3542.0, unrealizedPnl: 19.0, pnlPercent: 1.06, venue: 'Kraken' },
      ],
    },
  };
}

export function mockGetPortfolio(): ToolResult {
  return {
    tool: 'mockGetPortfolio',
    executedAt: new Date().toISOString(),
    data: {
      equity: 52480.25,
      cash: 15200.0,
      exposure: 37280.25,
      pnlDay: 847.3,
      pnlDayPct: 1.64,
      pnlTotal: 4780.5,
      var95: 2624.01,
      drawdownPct: 3.2,
      sharpeRatio: 1.42,
    },
  };
}

export function mockGetAlerts(): ToolResult {
  return {
    tool: 'mockGetAlerts',
    executedAt: new Date().toISOString(),
    data: {
      alerts: [
        { id: 'alert-1', severity: 'error', title: 'Agent Stopped', message: 'ETH Momentum Beta stopped due to insufficient API permissions', category: 'bot_lifecycle', timestamp: new Date(Date.now() - 1800_000).toISOString() },
        { id: 'alert-2', severity: 'warning', title: 'High Drawdown', message: 'BTC Grid Alpha approaching max drawdown threshold (8.5% of 10%)', category: 'risk', timestamp: new Date(Date.now() - 3600_000).toISOString() },
        { id: 'alert-3', severity: 'info', title: 'Order Filled', message: 'SOL DCA buy order filled: 10 SOL at $148.25', category: 'order', timestamp: new Date(Date.now() - 5400_000).toISOString() },
      ],
    },
  };
}

export function mockGetConnections(): ToolResult {
  return {
    tool: 'mockGetConnections',
    executedAt: new Date().toISOString(),
    data: {
      connections: [
        { id: 'conn-1', name: 'Binance Main', type: 'exchange', status: 'connected', exchange: 'binance' },
        { id: 'conn-2', name: 'Kraken Trading', type: 'exchange', status: 'connected', exchange: 'kraken' },
        { id: 'conn-3', name: 'MetaMask Wallet', type: 'wallet', status: 'connected' },
      ],
    },
  };
}

export function mockGetAgentWorkflowContext(): ToolResult {
  return {
    tool: 'mockGetAgentWorkflowContext',
    executedAt: new Date().toISOString(),
    data: {
      attention: [
        '1 policy-gated ETH liquidity proposal awaits review',
        '1 BTC mandate breach was blocked before OMS routing',
        '1 BTC/ETH proposal routed in paper mode only',
      ],
      allowedActions: ['open evidence', 'draft mandate', 'prepare policy brief', 'pause agent'],
      blockedActions: ['direct live order routing', 'credential reads', 'policy bypass'],
    },
  };
}

export function mockGetConnectionPosture(): ToolResult {
  return {
    tool: 'mockGetConnectionPosture',
    executedAt: new Date().toISOString(),
    data: {
      totalConnections: 19,
      compliant: 19,
      violations: 0,
      stale: ['Bybit - Direct (API)', 'Kraken - Direct (API)'],
      posture: 'Read/trade-only credentials; withdrawal scopes blocked.',
    },
  };
}

export function mockGetStrategyCatalog(): ToolResult {
  return {
    tool: 'mockGetStrategyCatalog',
    executedAt: new Date().toISOString(),
    data: {
      candidates: [
        { name: 'VWAP Execution', risk: 'Low', action: 'Draft execution supervisor' },
        { name: 'Volatility Forecaster', risk: 'Low', action: 'Attach as sizing overlay' },
        { name: 'Regime Detection', risk: 'Medium', action: 'Backtest before mandate draft' },
      ],
    },
  };
}

export function mockGetBacktestSummary(): ToolResult {
  return {
    tool: 'mockGetBacktestSummary',
    executedAt: new Date().toISOString(),
    data: {
      selectedRuns: [
        { strategy: 'Mean Reversion', returnPct: 0.25, sharpe: 1.42, maxDrawdownPct: 0.08 },
        { strategy: 'Momentum Trading', returnPct: 0.18, sharpe: 1.18, maxDrawdownPct: 0.12 },
        { strategy: 'Breakout Trading', returnPct: 0.31, sharpe: 1.67, maxDrawdownPct: 0.16 },
      ],
      recommendedDraft: 'Breakout Trading paper-mode agent draft with tight drawdown and venue checks.',
    },
  };
}

export function mockGetMonitoringHealth(): ToolResult {
  return {
    tool: 'mockGetMonitoringHealth',
    executedAt: new Date().toISOString(),
    data: {
      status: 'Healthy',
      watchItems: ['Memory usage elevated', 'Network I/O high', 'Queue depth within bounds'],
      nextActions: ['prepare restart checklist', 'inspect affected agents', 'watch queue depth'],
    },
  };
}

export function mockGetComplianceQueue(): ToolResult {
  return {
    tool: 'mockGetComplianceQueue',
    executedAt: new Date().toISOString(),
    data: {
      openAlerts: 6,
      critical: 1,
      reviewFocus: ['AML alert', 'limits alert', 'behavioral alert'],
      safeActions: ['draft acknowledgement rationale', 'open evidence', 'export review packet'],
    },
  };
}

export function mockGetTaxReconciliation(): ToolResult {
  return {
    tool: 'mockGetTaxReconciliation',
    executedAt: new Date().toISOString(),
    data: {
      warnings: [
        'Three disposals still missing lot attribution',
        'Manual transfer pairs pending confirmation',
        'FX reference rate older than 24h for USD pairs',
      ],
      exportReadiness: 'Export can be prepared, but filing quality improves after warning review.',
    },
  };
}

export function mockGetReportSnapshot(): ToolResult {
  return {
    tool: 'mockGetReportSnapshot',
    executedAt: new Date().toISOString(),
    data: {
      drivers: [
        'Mean Reversion is the strongest strategy contributor',
        'Risk Hedger and Arb Satellite are bottom contributors',
        'Fees are concentrated on live-crypto and paper-exchange venues',
      ],
      nextActions: ['draft IC note', 'inspect underperforming agents', 'compare risk-adjusted returns'],
    },
  };
}

export function mockGetPageActionCatalog(route = '/overview'): ToolResult {
  const context = getAssistantRouteContext(route);
  const actions = getSuggestedPrompts(route).map((action) => ({
    actionId: action.actionId ?? action.text,
    label: action.text,
    capability: action.capability ?? 'Guide',
    description: action.description ?? 'Ask Rugira for guidance on this action.',
    executionMode: action.executionMode ?? 'brief_only',
    launchMode: action.launchMode ?? 'chat',
    category: action.category ?? 'analyze',
  }));

  return {
    tool: 'mockGetPageActionCatalog',
    executedAt: new Date().toISOString(),
    data: {
      route,
      label: context.label,
      headline: context.headline,
      actions,
      blockedFromChat: [
        'live order routing',
        'wallet signing',
        'credential or secret reads',
        'policy or approval bypass',
      ],
    },
  };
}
